import React from 'react';
import { createRoot } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';

import Marker from '@spa/shared/components/Search/Marker';
import { setHistoryPath } from '@spa/shared/utils/history';
import { MARKER_EVENTS } from '@spa/shared/constants/mapEvents';
import { makeKey } from '@spa/shared/utils/key';
import { colors } from '@spa/shared/theme';

const MAPBOX_STYLE = 'mapbox://styles/uniplaces/cjdg4b5qyg2vp2ss0zhgp2ud7';
mapboxgl.accessToken = process.env.MAPBOX_TOKEN;
const MARKER_ACTIVE_CLASS = 'marker--active';
const MARKER_ACTIVE_HOVER_EVENT_CLASS = 'marker--active--hover';
const MARKER_CLASS = 'uni-marker';

const PADDING_BOTTOM_MOBILE = 100;
let markersArray = [];

export const getMap = (container, center, isMobile) => {
  const map = new mapboxgl.Map({
    container,
    style: MAPBOX_STYLE,
    center,
    dragRotate: false,
    zoom: 10
  });

  const basePad = isMobile ? 10 : 20;
  const bottom = isMobile ? PADDING_BOTTOM_MOBILE : basePad;
  map.setPadding({ left: basePad, right: basePad, top: basePad, bottom });
  map.touchZoomRotate.disableRotation();
  map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));

  return map;
};

export const getBounds = bounds => new mapboxgl.LngLatBounds(bounds);

export const getMarker = marker => new mapboxgl.Marker(marker);

const addStyle = (className, zIndex) => ({ target }) => {
  target.style.zIndex = zIndex;
  target.firstChild?.classList.add(className);
};

const removeStyle = (className, zIndex) => ({ target }) => {
  target.style.zIndex = zIndex;
  target.firstChild.classList.remove(className);

  if (target.firstChild.classList.contains(MARKER_ACTIVE_CLASS)) {
    target.style.zIndex = 2;
  }
};

const addActive = addStyle(MARKER_ACTIVE_CLASS, 2);
const removeActive = removeStyle(MARKER_ACTIVE_CLASS, 1);
const addActiveHover = addStyle(MARKER_ACTIVE_HOVER_EVENT_CLASS, 2);
const removeActiveHover = removeStyle(MARKER_ACTIVE_HOVER_EVENT_CLASS, 1);

export const triggerMarkerEvent = eventName => marker => {
  if (!marker) {
    return;
  }

  const element = marker.getElement();
  const event = new Event(eventName);

  element.dispatchEvent(event);
};

export const setMarkerActive = triggerMarkerEvent(MARKER_EVENTS.ADD_ACTIVE);
export const setMarkerActiveHover = triggerMarkerEvent(MARKER_EVENTS.DOCUMENT_ADD_ACTIVE);
export const removeMarkerActiveHover = triggerMarkerEvent(MARKER_EVENTS.DOCUMENT_REMOVE_ACTIVE);

export const addMarkerEvents = element => {
  element.addEventListener(MARKER_EVENTS.ADD_ACTIVE, addActive);
  element.addEventListener(MARKER_EVENTS.REMOVE_ACTIVE, removeActive);
  element.addEventListener(MARKER_EVENTS.DOCUMENT_ADD_ACTIVE, addActiveHover, { passive: true });
  element.addEventListener(MARKER_EVENTS.DOCUMENT_REMOVE_ACTIVE, removeActiveHover, { passive: true });
};

export const removeMarkerEvents = element => {
  element.removeEventListener(MARKER_EVENTS.ADD_ACTIVE, addActive);
  element.removeEventListener(MARKER_EVENTS.REMOVE_ACTIVE, removeActive);
  element.removeEventListener(MARKER_EVENTS.DOCUMENT_ADD_ACTIVE, addActiveHover);
  element.removeEventListener(MARKER_EVENTS.DOCUMENT_REMOVE_ACTIVE, removeActiveHover);
};

export const documentAddActive = event => {
  setMarkerActiveHover(getMarkerByOfferId(markersArray, event.detail.offerId));
};

export const documentRemoveActive = event => {
  removeMarkerActiveHover(getMarkerByOfferId(markersArray, event.detail.offerId));
};

export const loadDocumentEvents = () => {
  removeDocumentEvents();
  document.addEventListener(MARKER_EVENTS.DOCUMENT_ADD_ACTIVE, documentAddActive);
  document.addEventListener(MARKER_EVENTS.DOCUMENT_REMOVE_ACTIVE, documentRemoveActive);
};

export const removeDocumentEvents = () => {
  document.removeEventListener(MARKER_EVENTS.DOCUMENT_ADD_ACTIVE, documentAddActive);
  document.removeEventListener(MARKER_EVENTS.DOCUMENT_REMOVE_ACTIVE, documentRemoveActive);
};

export const resetAllActiveMarkers = () => {
  const markers = document.querySelectorAll(`.${MARKER_ACTIVE_CLASS}`);
  const event = new Event(MARKER_EVENTS.REMOVE_ACTIVE);

  markers.forEach(marker => marker.parentNode.dispatchEvent(event));
};

export const getLngLatFromOffer = ({ attributes }, isResidences) => {
  if (isResidences) {
    const {
      offers: [
        {
          property: {
            coordinates: [lat, lng]
          }
        }
      ]
    } = attributes;

    return [lng, lat];
  }

  const {
    property: {
      coordinates: [lat, lng]
    }
  } = attributes;

  return [lng, lat];
};

export const getPropertiesFromOffer = ({ id, attributes }, isResidences) => {
  if (isResidences) {
    const {
      offers: [
        {
          accommodation_offer: {
            is_instant_booking: isInstantBooking,
            price: { amount, currency_code: currency }
          }
        }
      ]
    } = attributes;

    return { id, isInstantBooking, amount, currency };
  }

  const {
    accommodation_offer: {
      is_instant_booking: isInstantBooking,
      price: { amount, currency_code: currency }
    }
  } = attributes;

  return { id, isInstantBooking, amount, currency };
};

export const compareLngLat = ([lng1, lat1], [lng2, lat2]) => lng1 === lng2 && lat1 === lat2;

export const groupOffersBySameLocation = (acc, offer, order) => {
  const lnglat = getLngLatFromOffer(offer, offer.type === 'residences');
  const { id, isInstantBooking, amount, currency } = getPropertiesFromOffer(offer, offer.type === 'residences');
  const isSameLatLng = ({ lnglat: coordinates }) => compareLngLat(coordinates, lnglat);
  const index = acc.findIndex(isSameLatLng);

  if (index !== -1) {
    const oldOffer = acc[index];
    const newOffer = {
      ...oldOffer,
      count: oldOffer.count + 1,
      ids: oldOffer.ids.concat(offer.id),
      indexes: oldOffer.indexes.concat(order),
      prices: oldOffer.prices.concat(amount),
      isInstantBooking: oldOffer.isInstantBooking || isInstantBooking,
      offers: [...oldOffer.offers, offer]
    };

    return Object.assign([], acc, { [index]: newOffer });
  }

  return [
    ...acc,
    { id, lnglat, isInstantBooking, count: 1, ids: [id], prices: [amount], currency, indexes: [order], offers: [offer] }
  ];
};

export const getMarkerByOfferId = (markers, desired) => markers.find(marker => marker.offer.ids.includes(desired));

export const createMarkers = (map, offers, active, setActive, setActiveOffer) => {
  return offers.map(offer => {
    const oldMarker = markersArray.find(({ offer: { id } }) => id === offer.id);
    if (oldMarker) {
      return oldMarker.addTo(map);
    }

    const { ids, lnglat } = offer;
    const el = document.createElement('div');
    const isActive = ids.includes(active);
    const handleMarkerClick = event => {
      setActive(offer.id);
      setActiveOffer({ offer, event });
    };

    const isResidences = offer.offers && offer.offers[0]?.attributes.property.type === 'residence';

    const root = createRoot(el);
    root.render(
      <React.Fragment>
        <Marker
          onClick={handleMarkerClick}
          offer={offer}
          className={isActive ? MARKER_ACTIVE_CLASS : ''}
          isUniresidences={isResidences}
        />
        <div id={`popup-${offer.id}`} />
      </React.Fragment>
    );
    addMarkerEvents(el);

    if (isActive) {
      el.style.zIndex = 2;
    }

    el.classList.add(MARKER_CLASS);

    const marker = getMarker(el)
      .setLngLat(lnglat)
      .addTo(map);

    return Object.assign(marker, { offer });
  });
};

export const loadNewMarkers = (map, offers, setActiveMarker, setMarkers, setActiveOffer, active, allNew) => {
  markersArray = allNew ? removeAllMarkers() : updateMarkersArray(offers, active);

  const markers = createMarkers(
    map,
    offers.reduce(groupOffersBySameLocation, []),
    active,
    setActiveMarker,
    setActiveOffer
  );

  markersArray = [...markersArray, ...markers];
  map.resize();

  setMarkers(markersArray);
};

export const onMapMoved = (map, history) => {
  const [pathname] = history.asPath.split('?');
  const bounds = map.getBounds();

  const state = {
    ...history.query,
    page: undefined,
    ne: bounds
      .getNorthEast()
      .toArray()
      .reverse()
      .toString(),
    sw: bounds
      .getSouthWest()
      .toArray()
      .reverse()
      .toString(),
    key: makeKey()
  };

  setHistoryPath(history, pathname, state);
};

export const onMapClicked = (
  {
    originalEvent: {
      target: { tagName }
    }
  },
  setIsVisible
) => {
  if (tagName !== 'CANVAS') {
    return;
  }

  setIsVisible(false);
};

export const updateMarkersArray = (offers, active) => {
  return markersArray.reduce((acc, marker) => {
    const {
      offer: { id }
    } = marker;
    const offerExists = offers.find(offer => id === offer.id);
    if (offerExists || id === active) {
      return [...acc, marker];
    }

    removeMarkerEvents(marker.getElement());
    marker.remove();

    return acc;
  }, []);
};

export const removeAllMarkers = () => {
  markersArray.forEach(marker => {
    removeMarkerEvents(marker.getElement(), marker.offer.ids);
    marker.remove();
  });

  loadDocumentEvents();

  return [];
};

export const addUniversityRadiusMarker = (map, lng, lat) => {
  const color = colors.uniBlue;
  const scale = 1.2;
  const radiusInMeters = 2000;
  const element = document.createElement('div');
  const marker = new mapboxgl.Marker({ color, scale, element });

  map.addSource('university', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          id: 'university',
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          },
          properties: {
            title: 'University'
          }
        }
      ]
    }
  });

  map.addLayer({
    id: 'university-radius',
    type: 'circle',
    source: 'university',
    paint: {
      'circle-radius': {
        base: 2,
        stops: [
          [0, 0],
          [20, metersToPixelsAtMaxZoom(radiusInMeters, lat)]
        ]
      },
      'circle-color': colors.blueTeal,
      'circle-opacity': 0.4,
      'circle-stroke-color': colors.uniBlue,
      'circle-stroke-width': 0.5
    }
  });

  element.classList.add('university__marker');

  marker.setLngLat([lng, lat]);
  marker.addTo(map);
};

const metersToPixelsAtMaxZoom = (meters, latitude) => meters / 0.075 / Math.cos((latitude * Math.PI) / 180);

export const getCircleSource = lngLat => ({
  type: 'geojson',
  data: {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: lngLat
        }
      }
    ]
  }
});

export const getCircleLayer = (lngLat, radius) => {
  const metersToPixelsAtMaxZoom = (meters, latitude) => meters / 0.075 / Math.cos((latitude * Math.PI) / 180);
  const maxRadius = metersToPixelsAtMaxZoom(radius, lngLat[1]);

  return {
    id: 'location',
    type: 'circle',
    center: lngLat,
    source: 'location',
    paint: {
      'circle-radius': {
        stops: [
          [0, 0],
          [20, maxRadius]
        ],
        base: 2
      },
      'circle-color': colors.uniBlue,
      'circle-opacity': 0.3,
      'circle-stroke-color': colors.uniBlue
    }
  };
};
